import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Upload, Loader2, FileCheck2 } from "lucide-react";

const labelCls = "block text-sm text-brand-ink mb-2";
const hintCls = "text-[11px] text-brand-subtle/80 mt-1.5";
const inputCls = "border-brand-line rounded-md bg-white focus-visible:ring-brand-moss/30";

export function FieldRenderer({ field, value, onChange, onUpload, assets = [] }) {
  const set = (v) => onChange(field.name, v);

  if (field.type === "text" || field.type === "number") {
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}{field.required && <span className="text-brand-moss"> *</span>}</label>
        <Input
          type={field.type === "number" ? "number" : "text"}
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => set(field.type === "number" ? e.target.value : e.target.value)}
          className={inputCls}
          data-testid={`input-${field.name}`}
        />
        {field.hint && <div className={hintCls}>{field.hint}</div>}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}{field.required && <span className="text-brand-moss"> *</span>}</label>
        <Textarea rows={4} value={value ?? ""} onChange={(e) => set(e.target.value)}
          className={inputCls} data-testid={`input-${field.name}`} />
        {field.hint && <div className={hintCls}>{field.hint}</div>}
      </div>
    );
  }

  if (field.type === "list") {
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}</label>
        <Textarea rows={3} value={Array.isArray(value) ? value.join("\n") : value ?? ""}
          onChange={(e) => set(e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
          className={inputCls} placeholder="Une ligne par élément" data-testid={`input-${field.name}`} />
        {field.hint && <div className={hintCls}>{field.hint}</div>}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}{field.required && <span className="text-brand-moss"> *</span>}</label>
        <select value={value ?? ""} onChange={(e) => set(e.target.value)}
          data-testid={`input-${field.name}`}
          className="w-full h-10 px-3 rounded-md border border-brand-line bg-white text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-moss/30">
          <option value="">—</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}{field.required && <span className="text-brand-moss"> *</span>}</label>
        <div className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <button key={o} type="button" onClick={() => set(o)}
              data-testid={`radio-${field.name}-${o.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                value === o ? "border-brand-moss bg-brand-mossSoft text-brand-moss"
                            : "border-brand-line bg-white text-brand-subtle hover:text-brand-ink"}`}>
              {o}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "repeater") {
    const rows = Array.isArray(value) ? value : [];
    const update = (i, k, v) => set(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
    return (
      <div data-testid={`field-${field.name}`}>
        <label className={labelCls}>{field.label}</label>
        <div className="space-y-3">
          {rows.map((row, i) => (
            <div key={i} className="rounded-md border border-brand-line bg-white p-3 grid gap-2 sm:grid-cols-3">
              {field.itemFields.map((f) => (
                <Input key={f.name} value={row[f.name] ?? ""} placeholder={f.label}
                  onChange={(e) => update(i, f.name, e.target.value)}
                  className={inputCls} data-testid={`repeater-${field.name}-${i}-${f.name}`} />
              ))}
              <button type="button" onClick={() => set(rows.filter((_, idx) => idx !== i))}
                className="sm:col-span-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle hover:text-red-600 transition-colors"
                data-testid={`repeater-remove-${field.name}-${i}`}>
                <Trash2 className="h-3 w-3" /> Retirer
              </button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => set([...rows, {}])}
          data-testid={`repeater-add-${field.name}`}
          className="mt-3 border-brand-line rounded-md text-[10px] font-mono uppercase tracking-[0.14em]">
          <Plus className="h-3.5 w-3.5 mr-2" /> Ajouter
        </Button>
      </div>
    );
  }

  if (field.type === "upload") {
    return <UploadField field={field} onUpload={onUpload} assets={assets} />;
  }

  return null;
}

function UploadField({ field, onUpload, assets }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const mine = assets.filter((a) => a.kind === field.kind && !a.is_deleted);

  const pick = async (files) => {
    setBusy(true);
    for (const f of Array.from(files)) await onUpload(f, field.kind);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  return (
    <div data-testid={`field-${field.name}`}>
      <label className={labelCls}>{field.label}</label>
      <input ref={ref} type="file" multiple={!!field.multiple} className="hidden"
        onChange={(e) => pick(e.target.files)} data-testid={`file-input-${field.kind}`} />
      <Button type="button" variant="outline" size="sm" disabled={busy}
        onClick={() => ref.current?.click()} data-testid={`upload-btn-${field.kind}`}
        className="border-brand-line rounded-md text-[10px] font-mono uppercase tracking-[0.14em]">
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-2" />}
        {mine.length ? "Ajouter un fichier" : "Choisir un fichier"}
      </Button>
      {mine.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid={`uploaded-${field.kind}`}>
          {mine.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-xs text-brand-ink/80">
              <FileCheck2 className="h-3.5 w-3.5 text-brand-moss flex-shrink-0" />
              <span className="truncate">{a.original_filename}</span>
              <span className="font-mono text-[10px] text-brand-subtle">{Math.round((a.size || 0) / 1024)} Ko</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
